import { create } from 'zustand';
import type { DepositLimitWindow } from '@/lib/depositLimit/types';

/**
 * Drives the deposit-limit popup (CP-007b). Like the territory gate, a deposit
 * cap can't self-resolve in-session, so there's no replay/await — the gate just
 * surfaces the breach and the original 403 propagates. Opened by the 403
 * DEPOSIT_LIMIT_EXCEEDED interceptor (server is authoritative).
 */
interface DepositLimitGatePayload {
  window: DepositLimitWindow;
  limitCents: number;
  remainingCents: number;
}

interface DepositLimitGateState {
  open: boolean;
  window: DepositLimitWindow | null;
  limitCents: number | null;
  remainingCents: number | null;
  openGate: (payload: DepositLimitGatePayload) => void;
  close: () => void;
}

const useDepositLimitGateStore = create<DepositLimitGateState>((set) => ({
  open: false,
  window: null,
  limitCents: null,
  remainingCents: null,
  openGate: (payload) => set({ open: true, ...payload }),
  close: () => set({ open: false, window: null, limitCents: null, remainingCents: null }),
}));

export const useDepositLimitGateOpen = () => useDepositLimitGateStore((s) => s.open);
export const useDepositLimitGateWindow = () => useDepositLimitGateStore((s) => s.window);
export const useDepositLimitGateLimitCents = () => useDepositLimitGateStore((s) => s.limitCents);
export const useDepositLimitGateRemainingCents = () => useDepositLimitGateStore((s) => s.remainingCents);
export const useCloseDepositLimitGate = () => useDepositLimitGateStore((s) => s.close);

/** Non-hook accessors for the 403 interceptor (outside React). */
export const openDepositLimitGateImperative = (payload: DepositLimitGatePayload) =>
  useDepositLimitGateStore.getState().openGate(payload);
export const closeDepositLimitGateImperative = () => useDepositLimitGateStore.getState().close();
